import React, { useEffect, useState } from "react";
import { CalendarClock, Check, X, Clock, Trash2, Save, CalendarRange, UserCog } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, num } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Badge } from "@/components/common";

const WD = [["Sen", 0], ["Sel", 1], ["Rab", 2], ["Kam", 3], ["Jum", 4], ["Sab", 5], ["Min", 6]];

export default function Attendance() {
  const { branch, isOwner } = useAuth();
  const [tab, setTab] = useState("today");
  const canRecap = isOwner;
  const tabs = [["today", "Hari Ini"], ["monthly", "Rekap Bulanan"], ...(canRecap ? [["range", "Rekap Rentang"]] : []), ["overtime", "Lembur"], ...(isOwner ? [["schedule", "Jadwal Kerja"]] : [])];
  return (
    <>
      <PageHeader title="Absensi & Lembur" subtitle={branch?.name} icon={CalendarClock} />
      <div className="flex gap-2 mb-4 flex-wrap">
        {tabs.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} className={`px-4 h-9 rounded-xl text-sm font-medium ${tab === v ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`att-tab-${v}`}>{l}</button>
        ))}
      </div>
      {tab === "today" && <TodayTab branch={branch} isOwner={isOwner} />}
      {tab === "monthly" && <MonthlyTab branch={branch} />}
      {tab === "range" && <RangeRecapTab branch={branch} />}
      {tab === "overtime" && <OvertimeTab branch={branch} isOwner={isOwner} />}
      {tab === "schedule" && <ScheduleTab branch={branch} />}
    </>
  );
}

function TodayTab({ branch, isOwner }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/attendance", branch ? { branch_id: branch.id } : {}).then(setList).finally(() => setLoading(false));
  };
  useEffect(load, [branch]);
  const del = async (a) => {
    if (!window.confirm(`Hapus catatan absensi ${a.user_name} (${a.type === "in" ? "Masuk" : "Keluar"})? Tidak bisa dibatalkan.`)) return;
    try { await api.del(`/attendance/${a.id}`); toast.success("Absensi dihapus"); load(); }
    catch (e) { toast.error(e.message); }
  };
  if (loading) return <Loading />;
  if (!list.length) return <EmptyState icon={CalendarClock} title="Belum ada absensi" message="Absensi (clock-in/out) dilakukan lewat aplikasi mobile dengan selfie & GPS." />;
  return (
    <Card className="overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
          <th className="py-3 px-4 font-medium">Karyawan</th><th className="py-3 px-4 font-medium">Tipe</th>
          <th className="py-3 px-4 font-medium">Waktu</th><th className="py-3 px-4 font-medium">Area</th><th className="py-3 px-4 font-medium text-right">Jam Kerja</th>
          {isOwner && <th className="py-3 px-4 font-medium text-right">Aksi</th>}
        </tr></thead>
        <tbody>
          {list.map((a) => (
            <tr key={a.id} className="border-b border-slate-50" data-testid={`att-row-${a.id}`}>
              <td className="py-3 px-4 font-medium text-slate-800">{a.user_name}</td>
              <td className="py-3 px-4"><Badge className={a.type === "in" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>{a.type === "in" ? "Masuk" : "Keluar"}</Badge>{a.late && <Badge className="bg-rose-100 text-rose-700 ml-1">Telat</Badge>}</td>
              <td className="py-3 px-4 text-slate-500">{fmtDateTime(a.created_at)}</td>
              <td className="py-3 px-4">{a.inside_area === false ? <Badge className="bg-amber-100 text-amber-700">Luar area</Badge> : a.inside_area ? <Badge className="bg-emerald-100 text-emerald-700">Dalam</Badge> : "-"}</td>
              <td className="py-3 px-4 text-right">{a.work_hours ? `${a.work_hours} jam` : "-"}</td>
              {isOwner && <td className="py-3 px-4 text-right"><button onClick={() => del(a)} className="w-8 h-8 rounded-lg hover:bg-rose-50 inline-flex items-center justify-center text-rose-500" data-testid={`delete-att-${a.id}`} title="Hapus (Owner)"><Trash2 size={15} /></button></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function MonthlyTab({ branch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  useEffect(() => {
    setLoading(true);
    const p = { month }; if (branch) p.branch_id = branch.id;
    api.get("/attendance/monthly", p).then(setData).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [branch, month]);
  return (
    <>
      <div className="mb-4"><input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 px-3 rounded-xl border border-slate-300 text-sm" /></div>
      {loading ? <Loading /> : !data ? null : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
              <th className="py-3 px-4 font-medium">Karyawan</th><th className="py-3 px-4 font-medium text-right">Hadir</th><th className="py-3 px-4 font-medium text-right">Absen</th>
              <th className="py-3 px-4 font-medium text-right">Telat</th><th className="py-3 px-4 font-medium text-right">Jam</th><th className="py-3 px-4 font-medium text-right">Lembur (disetujui)</th>
            </tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.user_id} className="border-b border-slate-50">
                  <td className="py-3 px-4"><div className="font-medium text-slate-800">{r.name}</div><div className="text-xs text-slate-400">{r.position || r.role}</div></td>
                  <td className="py-3 px-4 text-right">{r.days}</td><td className="py-3 px-4 text-right text-rose-600">{r.absent}</td>
                  <td className="py-3 px-4 text-right text-amber-600">{r.late}</td><td className="py-3 px-4 text-right">{num(r.hours)}</td>
                  <td className="py-3 px-4 text-right text-emerald-600">{num(r.ot_approved)}{r.ot_pending > 0 && <span className="text-amber-500"> (+{num(r.ot_pending)} pending)</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

function OvertimeTab({ branch, isOwner }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/attendance/overtime", branch ? { branch_id: branch.id } : {}).then(setData).finally(() => setLoading(false));
  };
  useEffect(load, [branch]);
  const decide = async (oid, decision) => {
    let notes = "";
    if (decision === "reject") { notes = window.prompt("Alasan penolakan?") || ""; if (!notes) return; }
    try { await api.post(`/attendance/overtime/${oid}/decide`, { decision, notes }); toast.success(decision === "approve" ? "Lembur disetujui" : "Lembur ditolak"); load(); }
    catch (e) { toast.error(e.message); }
  };
  if (loading) return <Loading />;
  if (!data?.rows?.length) return <EmptyState icon={Clock} title="Tidak ada lembur" />;
  return (
    <div className="space-y-2">
      {data.rows.map((o) => (
        <Card key={o.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`ot-${o.id}`}>
          <div>
            <div className="font-medium text-slate-800">{o.user_name} · {o.date}</div>
            <div className="text-xs text-slate-400">Kerja {o.work_hours} jam · Lembur {o.hours} jam · {o.branch_name}</div>
          </div>
          <div className="flex items-center gap-2">
            {o.status === "pending" ? (
              isOwner ? (
                <>
                  <Button size="sm" onClick={() => decide(o.id, "approve")}><Check size={15} /> Setujui</Button>
                  <Button size="sm" variant="danger" onClick={() => decide(o.id, "reject")}><X size={15} /> Tolak</Button>
                </>
              ) : <Badge className="bg-amber-100 text-amber-700">Menunggu Owner</Badge>
            ) : (
              <Badge className={o.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>{o.status === "approved" ? `Disetujui ${o.approved_hours} jam` : "Ditolak"}</Badge>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

const PRESETS = [
  ["Hari ini", 0], ["7 hari", 6], ["30 hari", 29], ["3 bulan", 89], ["6 bulan", 179], ["1 tahun", 364],
];
function isoDate(d) { return d.toISOString().slice(0, 10); }

function RangeRecapTab({ branch }) {
  const today = isoDate(new Date());
  const [from, setFrom] = useState(isoDate(new Date(Date.now() - 29 * 864e5)));
  const [to, setTo] = useState(today);
  const [scope, setScope] = useState("branch");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const bid = scope === "branch" ? branch?.id : undefined;

  useEffect(() => {
    setLoading(true);
    api.get("/attendance/recap", { date_from: from, date_to: to, branch_id: bid })
      .then(setData).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [from, to, bid]);

  const applyPreset = (days) => { setFrom(isoDate(new Date(Date.now() - days * 864e5))); setTo(today); };
  const t = data?.totals || {};

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-3">
        {PRESETS.map(([l, d]) => (
          <button key={l} onClick={() => applyPreset(d)} className="px-3 h-9 rounded-xl text-sm bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid={`recap-preset-${d}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="recap-from" />
        <span className="text-slate-400">s/d</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="recap-to" />
        <div className="flex gap-1 ml-auto">
          <button onClick={() => setScope("branch")} className={`px-3 h-9 rounded-xl text-sm ${scope === "branch" ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`} data-testid="recap-scope-branch">{branch?.name || "Cabang ini"}</button>
          <button onClick={() => setScope("all")} className={`px-3 h-9 rounded-xl text-sm ${scope === "all" ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`} data-testid="recap-scope-all">Semua cabang</button>
        </div>
      </div>
      {loading ? <Loading /> : !data ? null : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {[["Karyawan", t.staff ?? 0], ["Total hadir", `${t.days ?? 0} hari`], ["Jam kerja", `${num(t.hours ?? 0)} j`], ["Lembur disetujui", `${num(t.ot_approved ?? 0)} j`]].map(([l, v]) => (
              <Card key={l} className="py-3"><div className="text-xs text-slate-400">{l}</div><div className="text-lg font-bold text-slate-800">{v}</div></Card>
            ))}
          </div>
          <div className="text-xs text-slate-400 mb-2">Periode {data.from} s/d {data.to} · {data.elapsed_days} hari · {data.branch_name}</div>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
                <th className="py-3 px-4 font-medium">Karyawan</th><th className="py-3 px-4 font-medium text-right">Hadir</th><th className="py-3 px-4 font-medium text-right">Absen</th>
                <th className="py-3 px-4 font-medium text-right">Telat</th><th className="py-3 px-4 font-medium text-right">Jam</th><th className="py-3 px-4 font-medium text-right">Lembur ✓</th>
              </tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.user_id} className="border-b border-slate-50" data-testid={`recap-row-${r.user_id}`}>
                    <td className="py-3 px-4"><div className="font-medium text-slate-800">{r.name}</div><div className="text-xs text-slate-400">{r.position || r.role} · {r.branch_name}</div></td>
                    <td className="py-3 px-4 text-right">{r.days}</td><td className="py-3 px-4 text-right text-rose-600">{r.absent}</td>
                    <td className="py-3 px-4 text-right text-amber-600">{r.late}</td><td className="py-3 px-4 text-right">{num(r.hours)}</td>
                    <td className="py-3 px-4 text-right text-emerald-600">{num(r.ot_approved)}{r.ot_pending > 0 && <span className="text-amber-500"> (+{num(r.ot_pending)})</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </>
  );
}

function ScheduleTab({ branch }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/staff/schedules", branch ? { branch_id: branch.id } : {}).then(setStaff).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  };
  useEffect(load, [branch]);
  if (loading) return <Loading />;
  if (!staff.length) return <EmptyState icon={UserCog} title="Belum ada staf" message="Tambahkan karyawan di menu Karyawan terlebih dulu." />;
  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-500 flex items-center gap-2"><CalendarRange size={16} /> Atur jam masuk, total jam kerja standar, & hari libur mingguan per staf. Lembur dihitung otomatis bila lewat jam standar atau kerja di hari liburnya.</div>
      {staff.map((u) => <ScheduleRow key={u.id} u={u} onSaved={load} />)}
    </div>
  );
}

function ScheduleRow({ u, onSaved }) {
  const sc = u.work_schedule || {};
  const [shiftStart, setShiftStart] = useState(sc.shift_start || "");
  const [shiftHours, setShiftHours] = useState(sc.shift_hours ?? "");
  const [daysOff, setDaysOff] = useState(sc.days_off || []);
  const [notes, setNotes] = useState(sc.notes || "");
  const [busy, setBusy] = useState(false);

  const toggle = (d) => setDaysOff((xs) => xs.includes(d) ? xs.filter((x) => x !== d) : [...xs, d]);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/staff/${u.id}/schedule`, { shift_start: shiftStart || null, shift_hours: shiftHours === "" ? null : Number(shiftHours), days_off: daysOff, notes });
      toast.success(`Jadwal ${u.name} tersimpan`);
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Card data-testid={`schedule-${u.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div><div className="font-semibold text-slate-800">{u.name}</div><div className="text-xs text-slate-400">{u.position || u.role_name || u.role} · {u.branch_name}</div></div>
        <Button size="sm" onClick={save} disabled={busy} data-testid={`schedule-save-${u.id}`}><Save size={15} /> {busy ? "..." : "Simpan"}</Button>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div><span className="text-xs text-slate-500 mb-1 block">Jam masuk</span><input type="time" value={shiftStart} onChange={(e) => setShiftStart(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid={`schedule-start-${u.id}`} /></div>
        <div><span className="text-xs text-slate-500 mb-1 block">Jam kerja/hari</span><input type="number" step="0.5" min="0" max="24" value={shiftHours} onChange={(e) => setShiftHours(e.target.value)} placeholder="8" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid={`schedule-hours-${u.id}`} /></div>
      </div>
      <span className="text-xs text-slate-500 mb-1 block">Hari libur mingguan</span>
      <div className="flex gap-1.5 flex-wrap mb-3">
        {WD.map(([l, d]) => (
          <button key={d} type="button" onClick={() => toggle(d)} className={`px-3 h-8 rounded-full text-xs font-medium border ${daysOff.includes(d) ? "bg-rose-500 text-white border-rose-500" : "bg-white text-slate-600 border-slate-300"}`} data-testid={`schedule-day-${u.id}-${d}`}>{l}</button>
        ))}
      </div>
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Catatan jadwal (opsional)" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid={`schedule-notes-${u.id}`} />
    </Card>
  );
}

